// src/utils/strapiV5Utils.ts - Complete Strapi v5 Utilities với tested strategies
import axios from 'axios';

/**
 * ✅ Get documentId from record ID (Strapi v5 helper)
 * Trong Strapi v5, một số operations cần documentId, một số cần regular ID
 */
export const getDocumentId = async (
    baseURL: string,
    token: string | null,
    schema: string,
    recordId: string | number
): Promise<string> => {
    try {
        // Nếu recordId đã là documentId format (UUID), kiểm tra xem có tồn tại không
        if (typeof recordId === 'string' && recordId.includes('-') && recordId.length > 10) {
            console.log(`✅ Already documentId format: ${recordId}`);
            return recordId;
        }

        console.log(`🔍 Getting documentId for ${schema} record ${recordId}`);

        // Fetch record để lấy documentId
        const response = await axios.get(`${baseURL}/api/${schema}s/${recordId}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
            }
        });

        const documentId = response.data?.data?.documentId;

        if (!documentId) {
            console.warn(`⚠️ No documentId found for ${schema} record ${recordId}, using original ID`);
            return recordId.toString();
        }

        console.log(`✅ Found documentId: ${documentId} for record ${recordId}`);
        return documentId;
    } catch (error) {
        console.warn(`❌ Failed to get documentId for ${schema} record ${recordId}:`, error);
        return recordId.toString();
    }
};

/**
 * ✅ Get regular ID from documentId
 */
export const getRegularId = async (
    baseURL: string,
    token: string | null,
    schema: string,
    documentId: string
): Promise<number | string> => {
    try {
        console.log(`🔍 Getting regular ID for ${schema} documentId ${documentId}`);

        const response = await axios.get(`${baseURL}/api/${schema}s/${documentId}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
            }
        });

        const regularId = response.data?.data?.id;

        if (!regularId) {
            console.warn(`⚠️ No regular ID found for ${schema} documentId ${documentId}`);
            return documentId;
        }

        console.log(`✅ Found regular ID: ${regularId} for documentId ${documentId}`);
        return regularId;
    } catch (error) {
        console.warn(`❌ Failed to get regular ID for ${schema} documentId ${documentId}:`, error);
        return documentId;
    }
};

/**
 * ✅ Update record - Use regular ID (Tested: UPDATE works with regular ID)
 */
export const updateRecordById = async (
    baseURL: string,
    token: string | null,
    schema: string,
    recordId: string | number,
    updateData: any
): Promise<any> => {
    try {
        console.log(`🔄 Attempting to update ${schema} record ${recordId}`);

        // ✅ Strategy: Always get regular ID for UPDATE operations
        let actualId = recordId;

        // If we have documentId, get the regular ID
        if (typeof recordId === 'string' && recordId.includes('-') && recordId.length > 10) {
            console.log(`📄 Converting documentId to regular ID for UPDATE: ${recordId}`);

            try {
                actualId = await getRegularId(baseURL, token, schema, recordId);
                console.log(`✅ Will use regular ID for UPDATE: ${actualId}`);
            } catch (getError: any) {
                console.warn(`❌ Could not get regular ID from documentId, using original: ${recordId}`);
                actualId = recordId;
            }
        }

        // ✅ Perform UPDATE with regular ID
        console.log(`🔄 Executing PUT with ID: ${actualId}`);

        const response = await axios({
            method: 'PUT',
            url: `${baseURL}/api/${schema}s/${actualId}`,
            data: { data: updateData },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : '',
            }
        });

        console.log(`✅ Successfully updated ${schema} record with ID: ${actualId}`);
        return response.data;

    } catch (error: any) {
        console.error(`❌ Failed to update ${schema} record:`, error.response?.data || error.message);

        // ✅ Enhanced error message
        let errorMessage = getStrapiErrorMessage(error);

        if (error.response?.status === 404) {
            errorMessage = `Record not found for ${schema} ID: ${recordId}`;
        } else if (error.response?.status === 400) {
            errorMessage = `Invalid data provided for ${schema} update: ${errorMessage}`;
        }

        throw new Error(`Update failed for ${schema}: ${errorMessage}`);
    }
};

/**
 * ✅ Delete record - Use documentId (Tested: DELETE works with documentId)
 */
export const deleteRecordById = async (
    baseURL: string,
    token: string | null,
    schema: string,
    recordId: string | number
): Promise<any> => {
    try {
        console.log(`🗑️ Attempting to delete ${schema} record ${recordId}`);

        // ✅ Strategy: Use documentId for DELETE operations
        let actualId = recordId;

        // If we have regular ID, get the documentId
        if (typeof recordId === 'number' || (typeof recordId === 'string' && !recordId.includes('-'))) {
            console.log(`🔄 Converting regular ID to documentId for DELETE: ${recordId}`);

            try {
                actualId = await getDocumentId(baseURL, token, schema, recordId);
                console.log(`✅ Will use documentId for DELETE: ${actualId}`);
            } catch (getError: any) {
                console.warn(`❌ Could not get documentId from regular ID, using original: ${recordId}`);
                actualId = recordId;
            }
        }

        // ✅ Perform DELETE with documentId (or fallback to original)
        console.log(`🔄 Executing DELETE with ID: ${actualId}`);

        const response = await axios({
            method: 'DELETE',
            url: `${baseURL}/api/${schema}s/${actualId}`,
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
            }
        });

        console.log(`✅ Successfully deleted ${schema} record with ID: ${actualId}`);
        return response.data;

    } catch (error: any) {
        console.error(`❌ Failed to delete ${schema} record:`, error.response?.data || error.message);

        // ✅ Enhanced error message
        let errorMessage = getStrapiErrorMessage(error);

        if (error.response?.status === 404) {
            errorMessage = `Record not found for ${schema} ID: ${recordId}`;
        }

        throw new Error(`Delete failed for ${schema}: ${errorMessage}`);
    }
};

/**
 * ✅ Bulk update records using optimal ID strategy
 */
export const bulkUpdateRecords = async (
    baseURL: string,
    token: string | null,
    schema: string,
    recordIds: (string | number)[],
    updateData: any
): Promise<{
    results: Array<{ id: string | number; success: boolean; data?: any; error?: string }>;
    summary: { total: number; successful: number; failed: number };
}> => {
    console.log(`🔄 Bulk updating ${recordIds.length} ${schema} records`);

    const results = [];
    for (const id of recordIds) {
        try {
            const result = await updateRecordById(baseURL, token, schema, id, updateData);
            results.push({ id, success: true, data: result });
        } catch (error: any) {
            console.error(`Failed to update ${schema} record ${id}:`, error);
            results.push({ id, success: false, error: getStrapiErrorMessage(error) });
        }
    }

    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Bulk update completed: ${successCount}/${recordIds.length} successful`);

    return {
        results,
        summary: {
            total: recordIds.length,
            successful: successCount,
            failed: recordIds.length - successCount
        }
    };
};

/**
 * ✅ Bulk delete records using optimal ID strategy
 */
export const bulkDeleteRecords = async (
    baseURL: string,
    token: string | null,
    schema: string,
    recordIds: (string | number)[]
): Promise<{
    results: Array<{ id: string | number; success: boolean; error?: string }>;
    summary: { total: number; successful: number; failed: number };
}> => {
    console.log(`🗑️ Bulk deleting ${recordIds.length} ${schema} records`);

    const results = [];
    for (const id of recordIds) {
        try {
            await deleteRecordById(baseURL, token, schema, id);
            results.push({ id, success: true });
        } catch (error: any) {
            console.error(`Failed to delete ${schema} record ${id}:`, error);
            results.push({ id, success: false, error: getStrapiErrorMessage(error) });
        }
    }

    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Bulk delete completed: ${successCount}/${recordIds.length} successful`);

    return {
        results,
        summary: {
            total: recordIds.length,
            successful: successCount,
            failed: recordIds.length - successCount
        }
    };
};

/**
 * ✅ Check if identifier is documentId format
 */
export const isDocumentId = (identifier: string | number): boolean => {
    return typeof identifier === 'string' &&
        identifier.includes('-') &&
        identifier.length > 10;
};

/**
 * ✅ Get record identifier (documentId if available, otherwise id)
 */
export const getRecordIdentifier = (record: any): string => {
    return record?.documentId || record?.id || '';
};

/**
 * ✅ Get the optimal identifier for different operations
 */
export const getOptimalId = (record: any, operation: 'update' | 'delete' | 'get'): string => {
    const documentId = record?.documentId;
    const regularId = record?.id;

    switch (operation) {
        case 'update':
            // UPDATE works best with regular ID
            return regularId?.toString() || documentId || '';
        case 'delete':
            // DELETE works best with documentId
            return documentId || regularId?.toString() || '';
        case 'get':
        default:
            // GET works with both, prefer documentId
            return documentId || regularId?.toString() || '';
    }
};

/**
 * ✅ Enhanced error message extraction for Strapi v5
 */
export const getStrapiErrorMessage = (error: any): string => {
    let errorMessage = 'Có lỗi xảy ra. Vui lòng thử lại sau.';

    if (error.response?.data) {
        const { data } = error.response;

        if (data.error) {
            if (typeof data.error === 'string') {
                errorMessage = data.error;
            } else if (data.error.message) {
                errorMessage = data.error.message;

                if (data.error.details?.errors) {
                    const validationErrors = data.error.details.errors
                        .map((e: any) => {
                            const field = e.path?.join('.') || 'Unknown field';
                            return `${field}: ${e.message}`;
                        })
                        .join('; ');

                    errorMessage = `${errorMessage}. Chi tiết: ${validationErrors}`;
                }
            }
        } else if (data.message) {
            errorMessage = data.message;
        }
    } else if (error.message) {
        errorMessage = error.message;
    }

    // ✅ Localize Strapi v5 specific errors
    if (errorMessage.includes('documentId')) {
        errorMessage = `Lỗi documentId (Strapi v5): ${errorMessage}`;
    }

    if (errorMessage.includes('MinIO') || errorMessage.includes('S3')) {
        errorMessage = `Lỗi lưu trữ file: ${errorMessage}`;
    }

    return errorMessage;
};

/**
 * ✅ Validate schema name for Strapi API
 */
export const validateSchemaName = (schema: string): boolean => {
    // Basic validation for schema names
    const validPattern = /^[a-z][a-z0-9-]*$/;
    return validPattern.test(schema);
};

/**
 * ✅ Generate API endpoint for schema
 */
export const getSchemaEndpoint = (baseURL: string, schema: string): string => {
    if (!validateSchemaName(schema)) {
        throw new Error(`Invalid schema name: ${schema}`);
    }

    return `${baseURL}/api/${schema}s`;
};

/**
 * ✅ Parse Strapi v5 response data
 */
export const parseStrapiResponse = (response: any) => {
    return {
        data: response.data?.data,
        meta: response.data?.meta,
        pagination: response.data?.meta?.pagination,
        isSuccess: response.status >= 200 && response.status < 300
    };
};

/**
 * ✅ Format record for Strapi v5 API
 */
export const formatRecordForStrapi = (data: any) => {
    return {
        data: {
            ...data,
            
        }
    };
};

/**
 * ✅ Debug helper - analyze record IDs
 */
export const analyzeRecordId = (record: any) => {
    const id = record?.id;
    const documentId = record?.documentId;

    return {
        hasId: Boolean(id),
        hasDocumentId: Boolean(documentId),
        idType: typeof id,
        documentIdType: typeof documentId,
        isDocumentIdFormat: documentId ? isDocumentId(documentId) : false,
        recommendedForUpdate: id?.toString() || documentId,
        recommendedForDelete: documentId || id?.toString(),
        recommendedForGet: documentId || id?.toString()
    };
};

/**
 * ✅ Smart record fetcher - tries optimal ID for GET operations
 */
export const fetchRecord = async (
    baseURL: string,
    token: string | null,
    schema: string,
    identifier: string | number,
    populate: string = '*'
): Promise<any> => {
    try {
        console.log(`🔍 Fetching ${schema} record: ${identifier}`);

        const response = await axios.get(`${baseURL}/api/${schema}s/${identifier}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
            },
            params: {
                populate
            }
        });

        console.log(`✅ Successfully fetched ${schema} record`);
        return response.data?.data;
    } catch (error: any) {
        console.error(`❌ Failed to fetch ${schema} record ${identifier}:`, error.response?.data || error.message);
        throw new Error(`Fetch failed for ${schema}: ${getStrapiErrorMessage(error)}`);
    }
};

/**
 * ✅ Search records with filters
 */
export const searchRecords = async (
    baseURL: string,
    token: string | null,
    schema: string,
    filters: Record<string, any> = {},
    pagination: { page?: number; pageSize?: number } = {},
    populate: string = '*'
): Promise<{ data: any[], meta: any }> => {
    try {
        console.log(`🔍 Searching ${schema} records with filters:`, filters);

        const params: any = {
            populate,
            ...pagination
        };

        // Add filters
        Object.keys(filters).forEach(key => {
            params[`filters[${key}]`] = filters[key];
        });

        const response = await axios.get(`${baseURL}/api/${schema}s`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
            },
            params
        });

        console.log(`✅ Found ${response.data?.data?.length || 0} ${schema} records`);
        return {
            data: response.data?.data || [],
            meta: response.data?.meta || {}
        };
    } catch (error: any) {
        console.error(`❌ Failed to search ${schema} records:`, error.response?.data || error.message);
        throw new Error(`Search failed for ${schema}: ${getStrapiErrorMessage(error)}`);
    }
};

export default {
    // Core functions
    getDocumentId,
    getRegularId,
    updateRecordById,
    deleteRecordById,
    bulkUpdateRecords,
    bulkDeleteRecords,

    // Utility functions
    isDocumentId,
    getRecordIdentifier,
    getOptimalId,
    getStrapiErrorMessage,
    validateSchemaName,
    getSchemaEndpoint,
    parseStrapiResponse,
    formatRecordForStrapi,
    analyzeRecordId,

    // Enhanced functions
    fetchRecord,
    searchRecords
};
